import { inviteCodeColumns } from "@/auth/admin-invites-columns";
import {
  createInviteCode,
  listInviteCodes,
  revokeInviteCode,
  type CreatedInviteCode,
  type InviteCodeRow,
} from "@/auth/api/invite-api";
import { INVITE_ACCESS_LEVEL_LABELS, type InviteAccessLevel } from "@/auth/permissions";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader, PageShell } from "@/components/layout/PageHeader";
import { DataTable } from "@/components/table/DataTable";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineError,
  Input,
  NativeSelect,
  Spinner,
} from "@/components/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function AdminInvitesPage() {
  const [rows, setRows] = useState<InviteCodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maxUses, setMaxUses] = useState("1");
  const [accessLevel, setAccessLevel] = useState<InviteAccessLevel>("normal");
  const [note, setNote] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState<CreatedInviteCode | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listInviteCodes());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load invite codes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const handleCreate = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setCreating(true);
    setError(null);
    setCreatedCode(null);

    const parsedMaxUses = Number.parseInt(maxUses, 10);
    const parsedExpires =
      expiresInDays.trim() === "" ? undefined : Number.parseInt(expiresInDays, 10);

    if (!Number.isFinite(parsedMaxUses) || parsedMaxUses < 1) {
      setError("Max uses must be at least 1.");
      setCreating(false);
      return;
    }

    if (parsedExpires !== undefined && (!Number.isFinite(parsedExpires) || parsedExpires < 1)) {
      setError("Expiry days must be at least 1 when set.");
      setCreating(false);
      return;
    }

    try {
      const created = await createInviteCode({
        maxUses: parsedMaxUses,
        note: note.trim() || undefined,
        expiresInDays: parsedExpires,
        accessLevel,
      });
      setCreatedCode(created);
      setNote("");
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create invite code.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = useCallback(
    async (id: string): Promise<void> => {
      setRevokingId(id);
      setError(null);
      try {
        await revokeInviteCode(id);
        await loadRows();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not revoke invite code.");
      } finally {
        setRevokingId(null);
      }
    },
    [loadRows],
  );

  const columns = useMemo(
    () => inviteCodeColumns({ revokingId, onRevoke: (id) => void handleRevoke(id) }),
    [revokingId, handleRevoke],
  );

  const copyCode = async (code: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  return (
    <AppLayout>
      <PageShell className="max-w-5xl">
        <PageHeader
          title="Invite codes"
          description="Create multi-use codes for self-service signup at /signup."
        />

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h2 className="text-base font-semibold text-[var(--color-text)]">New invite code</h2>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                Share the code with new users. Each signup consumes one use.
              </p>
            </CardHeader>
            <CardBody>
              <form
                className="grid gap-4 md:grid-cols-[120px_140px_1fr_140px_auto]"
                onSubmit={(event) => void handleCreate(event)}
              >
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-[var(--color-text)]">Access</span>
                  <NativeSelect
                    value={accessLevel}
                    onChange={(event) => setAccessLevel(event.target.value as InviteAccessLevel)}
                  >
                    {(Object.keys(INVITE_ACCESS_LEVEL_LABELS) as InviteAccessLevel[]).map(
                      (level) => (
                        <option key={level} value={level}>
                          {INVITE_ACCESS_LEVEL_LABELS[level]}
                        </option>
                      ),
                    )}
                  </NativeSelect>
                </label>

                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-[var(--color-text)]">Max uses</span>
                  <Input
                    type="number"
                    min={1}
                    value={maxUses}
                    onChange={(event) => setMaxUses(event.target.value)}
                    required
                  />
                </label>

                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-[var(--color-text)]">
                    Note (optional)
                  </span>
                  <Input
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Allison onboarding"
                  />
                </label>

                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-[var(--color-text)]">
                    Expires (days)
                  </span>
                  <Input
                    type="number"
                    min={1}
                    value={expiresInDays}
                    onChange={(event) => setExpiresInDays(event.target.value)}
                    placeholder="Optional"
                  />
                </label>

                <div className="flex items-end">
                  <Button type="submit" disabled={creating}>
                    {creating ? "Creating…" : "Create code"}
                  </Button>
                </div>
              </form>

              {createdCode ? (
                <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
                  <p className="text-sm text-[var(--color-text-muted)]">
                    New code (copy and share):
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <code className="text-lg font-semibold tracking-[0.2em] text-[var(--color-text)]">
                      {createdCode.code}
                    </code>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void copyCode(createdCode.code)}
                    >
                      Copy
                    </Button>
                    <span className="text-sm text-[var(--color-text-muted)]">
                      {INVITE_ACCESS_LEVEL_LABELS[createdCode.access_level]} access ·{" "}
                      {createdCode.max_uses} use{createdCode.max_uses === 1 ? "" : "s"}
                      {createdCode.expires_at
                        ? ` · expires ${formatDate(createdCode.expires_at)}`
                        : ""}
                    </span>
                  </div>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {error ? <InlineError message={error} /> : null}

          <Card>
            <CardHeader>
              <h2 className="text-base font-semibold text-[var(--color-text)]">Existing codes</h2>
            </CardHeader>
            <CardBody>
              {loading ? (
                <Spinner label="Loading invite codes…" />
              ) : rows.length === 0 ? (
                <EmptyState
                  title="No invite codes yet"
                  description="Create a code above to enable self-service signup."
                />
              ) : (
                <DataTable
                  embedded
                  data={rows as unknown as Record<string, unknown>[]}
                  columns={columns as ColumnDef<Record<string, unknown>, unknown>[]}
                  searchColumnId="code"
                  searchPlaceholder="Search codes…"
                  exportFilename="invite-codes.csv"
                  emptyTitle="No matching codes"
                  emptyDescription="Try a different search term."
                  pageSize={25}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </PageShell>
    </AppLayout>
  );
}
